import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getLegalDocument } from '../services/legalService';

/**
 * Reusable read-only legal document viewer.
 * Renders backend-provided HTML (already fully styled with an inline <style> block).
 *
 * @param {'privacy_policy' | 'terms_of_service' | 'cookies_policy'} type
 * @param {string} defaultTitle  Header fallback when the API returns no title.
 */
export default function LegalDocument({ type, defaultTitle }) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'error' | 'success'
  const [doc, setDoc] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchDoc = useCallback(async () => {
    setStatus('loading');
    setErrorMessage('');
    try {
      const response = await getLegalDocument(type);
      const data = response?.data;
      if (!data || !data.content) {
        throw new Error('This document is not available yet.');
      }
      setDoc(data);
      setStatus('success');
    } catch (err) {
      setErrorMessage(err?.message || 'Failed to load this document. Please try again.');
      setStatus('error');
    }
  }, [type]);

  useEffect(() => {
    fetchDoc();
  }, [fetchDoc]);

  const title = (status === 'success' && doc?.title) || defaultTitle;

  return (
    <div className="legal-page">
      <header className="legal-header">
        {/* <Link to="/dashboard" className="legal-back" aria-label="Go back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Link> */}
        <h1 className="legal-title">{title}</h1>
      </header>

      <div className="legal-body">
        {status === 'loading' && (
          <div className="legal-state">
            <div className="legal-spinner" aria-label="Loading" />
            <p>Loading {title.toLowerCase()}…</p>
          </div>
        )}

        {status === 'error' && (
          <div className="legal-state">
            <p className="legal-error-text">{errorMessage}</p>
            <button type="button" className="legal-retry-btn" onClick={fetchDoc}>
              Retry
            </button>
          </div>
        )}

        {status === 'success' && (
          <div
            className="legal-content"
            dangerouslySetInnerHTML={{ __html: doc.content }}
          />
        )}
      </div>
    </div>
  );
}
